/**
 * Router, providers and route guards.
 *
 * Route map
 *   public      /login  /setup  /accept-invite
 *   auth only   /onboarding                        (no workspace required yet)
 *   app shell   /  /composer  /posts  /calendar  /media  /connections
 *               /brand  /style  /team  /settings  /admin (superadmin)
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from './components/layout';
import { Spinner, ToastProvider } from './components/ui';
import { queryClient } from './lib/query';
import { applyTheme, useSessionStore } from './stores/session';
import { useWorkspaceStore } from './stores/workspace';

import { DashboardPage } from './pages/Dashboard';
import { ComposerPage } from './pages/ComposerPage';
import { PostsPage } from './pages/Posts';
import { CalendarPage } from './pages/Calendar';
import { MediaPage } from './pages/Media';
import { ConnectionsPage } from './pages/Connections';
import { BrandPage } from './pages/Brand';
import { StylePage } from './pages/Style';
import { TeamPage } from './pages/Team';
import { SettingsPage } from './pages/Settings';
import { AdminPage } from './pages/Admin';
import { LoginPage } from './pages/Login';
import { SetupPage } from './pages/Setup';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { MagicContinuePage } from './pages/MagicContinue';
import { OnboardingPage } from './pages/Onboarding';
import { NotFoundPage } from './pages/NotFound';

/* ------------------------------------------------------------------ */
/* Bootstrap                                                           */
/* ------------------------------------------------------------------ */

function FullPageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <Spinner label="Loading your workspace" />
    </div>
  );
}

/** Loads /api/auth/me once and keeps the active workspace valid. */
function SessionGate({ children }: { children: ReactNode }) {
  const status = useSessionStore((state) => state.status);
  const workspaces = useSessionStore((state) => state.workspaces);
  const theme = useSessionStore((state) => state.theme);
  const bootstrap = useSessionStore((state) => state.bootstrap);
  const syncWithAvailable = useWorkspaceStore((state) => state.syncWithAvailable);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    syncWithAvailable(workspaces);
  }, [workspaces, syncWithAvailable]);

  if (status === 'idle' || status === 'loading') return <FullPageLoader />;
  return <>{children}</>;
}

/* ------------------------------------------------------------------ */
/* Guards                                                              */
/* ------------------------------------------------------------------ */

/** Signed-in users only. Sends first-run deployments to /setup. */
function RequireAuth() {
  const status = useSessionStore((state) => state.status);
  const setupRequired = useSessionStore((state) => state.setupRequired);
  const location = useLocation();

  if (setupRequired) return <Navigate to="/setup" replace />;
  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/** Requires at least one workspace — otherwise run the onboarding wizard. */
function RequireWorkspace() {
  const workspaces = useSessionStore((state) => state.workspaces);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);

  if (workspaces.length === 0) return <Navigate to="/onboarding" replace />;
  if (!workspaceId) return <FullPageLoader />;
  return <Outlet />;
}

function RequireSuperadmin() {
  const user = useSessionStore((state) => state.user);
  if (!user?.isSuperadmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Keeps signed-in users out of /login and /setup. */
function PublicOnly() {
  const status = useSessionStore((state) => state.status);
  const setupRequired = useSessionStore((state) => state.setupRequired);
  const location = useLocation();

  if (status === 'authenticated' && !setupRequired) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }
  return <Outlet />;
}

/* ------------------------------------------------------------------ */
/* Routes                                                              */
/* ------------------------------------------------------------------ */

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route element={<PublicOnly />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup" element={<SetupPage />} />
      </Route>
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/auth/continue" element={<MagicContinuePage />} />

      {/* Signed in */}
      <Route element={<RequireAuth />}>
        <Route path="/onboarding" element={<OnboardingPage />} />

        <Route element={<RequireWorkspace />}>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />
            <Route path="/composer" element={<ComposerPage />} />
            <Route path="/composer/:postId" element={<ComposerPage />} />
            <Route path="/posts" element={<PostsPage />} />
            <Route path="/posts/:postId" element={<PostsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/media" element={<MediaPage />} />
            <Route path="/media/:assetId" element={<MediaPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/brand" element={<BrandPage />} />
            <Route path="/style" element={<StylePage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route element={<RequireSuperadmin />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <SessionGate>
            <AppRoutes />
          </SessionGate>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
